import { useEffect, useState } from "react";
import { Card, Col, ListGroup, Row } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from '../../../../../hooks/useApiClient';
import useStore from '../../../../../store/TiassistoStore';
import { PudPickupState, PudStatusId } from "../../../../../api/models/Enums";
import TableSkeleton from "../../../../TableSkeleton/TableSkeleton";

const DetailsPud = ({ setIsDeleteButtonEnabled, isOpen, onClose }) => {
  const { t } = useTranslation();
  const { apiClient } = useApiClient();
  const { pudId } = useStore();
  const queryClient = useQueryClient();
  const [dataPud, setDataPud] = useState<any>({});

  // Query single PUD by pudId
  const { data, isPending: isPudListsLoading, error: pudListError, isSuccess } = useQuery({
    queryKey: ['singlePud'],
    queryFn: () => apiClient.tiassistoController.getPudDetails(pudId),
    enabled: !!pudId && isOpen,
    retry: false,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Query maintenance categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['getMaintenanceCategories'],
    queryFn: () => apiClient.tiassistoController.getPudMaintenanceCategories(),
    enabled: !!isSuccess,
    retry: false,
  });

  // Query maintenance subcategories
  const { data: subCategories, isLoading: subCategoriesLoading } = useQuery({
    queryKey: ['getMaintenanceSubCategories', data?.bookMaintenanceCategoryId],
    queryFn: () => apiClient.tiassistoController.getPudMaintenanceSubCategories(data?.bookMaintenanceCategoryId),
    enabled: !!data?.bookMaintenanceCategoryId,
    retry: false,
  });

  // Query PUD statuses
  const { data: pudStatuses, isLoading: pudStatusesLoading } = useQuery({
    queryKey: ['pudStatuses', data?.statusId],
    queryFn: () => apiClient.tiassistoController.getPudStatuses(),
    enabled: !!data?.statusId,
    retry: false,
  });


  useEffect(() => {
    if (isSuccess && categories && subCategories && pudStatuses) {
      // Map category and subcategory names
      const categoryName = categories.categories.find(
        (category) => parseInt(data?.bookMaintenanceCategoryId) === parseInt(category.id)
      )?.name;

      const subCategoryName = subCategories.subcategories.find(
        (subCategory) => parseInt(data?.bookMaintenanceSubCategoryId) === parseInt(subCategory.id)
      )?.name;

      const statusDescription = pudStatuses.statuses.find((status) => Number(status.statusId) === parseInt(data?.statusId))?.statusDescription;

      // Update PUD data with category names
      setDataPud({
        ...data,
        bookMaintenanceCategoryId: categoryName,
        bookMaintenanceSubCategoryId: subCategoryName,
        statusDescription,
      });

      // Determine if the delete button should be disabled
      // PUD can be deleted if in state “In Attesa” o “Confermata”, but “Stato ritiro” in “Da programmare”
      const shouldDisableDeleteButton =
        [PudStatusId.IN_ATTESA, PudStatusId.CONFERMATA].includes(data.statusId as PudStatusId) &&
        data.pickup_state === PudPickupState.DA_PROGRAMMARE;

      setIsDeleteButtonEnabled(!shouldDisableDeleteButton);
    }
  }, [isSuccess, categories, subCategories, pudStatuses, data, setIsDeleteButtonEnabled]);

  useEffect(() => {
    return () => {
      if (!isOpen) {
        queryClient.removeQueries({ queryKey: ['singlePud'] });
      }
    };
  }, [isOpen, queryClient]);

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        {isPudListsLoading || categoriesLoading || subCategoriesLoading ? (
          <TableSkeleton cols={2} rows={3} />
        ) : (
          <Row>
            <Col>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <div>
                    <strong>{t("pud_id")}:</strong> {dataPud?.pudId}
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div>
                    <b>{t("category")}:</b> {dataPud?.bookMaintenanceCategoryId}
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div>
                    <b>{t("sub_category")}:</b> {dataPud?.bookMaintenanceSubCategoryId}
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div>
                    <b>{t("partner")}:</b> {dataPud?.partnerId}
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div>
                    <b>{t("procedure_status")}:</b> {dataPud?.statusDescription}
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div>
                    <b>{t("notes")}:</b> {dataPud?.note}
                  </div>
                </ListGroup.Item>
              </ListGroup>

            </Col>
            <Col>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <div>
                    <b>{t("pickup_state")}:</b> {dataPud?.pickup_state}
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div>
                    <b>{t("pickup_date")}:</b> {dataPud?.pickup_date}
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div>
                    <b>{t("delivery_state")}:</b> {dataPud?.delivery_state}
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div>
                    <b>{t("delivery_date")}:</b> {dataPud?.delivery_date}
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div>
                    <b>{t("pickup_address")}:</b> {dataPud?.pickup_delivery_address}
                  </div>
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>

        )}
      </Card.Body>
      <Card.Footer>
        <small className="text-muted">
          <div>
            <b>{t("creation_date")}:</b> {dataPud?.created_at}
          </div>
          <div>
            <b>{t("update_date")}:</b> {dataPud?.updated_at}
          </div>
        </small>
      </Card.Footer>
    </Card>
  );
};

export default DetailsPud;
